import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  interviewsApi,
  type CreateInterviewInput,
  type ListInterviewsParams,
} from "@/lib/api/interviews";
import { mailboxKeys } from "./use-mailbox";

/**
 * Interview lifecycle query/mutation hooks (ENG-144).
 *
 * These cover the interview LIFECYCLE only (create/associate, transcript, list,
 * reopen). The live conversation itself — the user's answers — flows through the
 * existing job mailbox hooks in {@link ./use-mailbox} (`usePostTurn`), never
 * through here. That is the single turn path required by the architecture.
 *
 * ENG-145 (transcript history / reuse) can reuse {@link useInterviews} and the
 * `interviewKeys` factory below for its history list.
 */

// =============================================================================
// Query Keys
// =============================================================================

export const interviewKeys = {
  all: ["interviews"] as const,
  lists: () => [...interviewKeys.all, "list"] as const,
  list: (params: ListInterviewsParams) =>
    [...interviewKeys.lists(), params] as const,
  details: () => [...interviewKeys.all, "detail"] as const,
  detail: (id: string) => [...interviewKeys.details(), id] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Lists the caller's interviews (history). Used for resume detection (filter by
 * `status: "in_progress"`) and, later, the ENG-145 history page. `enabled` lets
 * callers defer the fetch (e.g. only while a dialog is open).
 */
export function useInterviews(
  params: ListInterviewsParams = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: interviewKeys.list(params),
    queryFn: () => interviewsApi.list(params),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Fetches a single interview transcript. While the interview is in_progress the
 * server returns the LIVE job turns; the mailbox `useJob` hook remains the
 * primary live poller for the conversation, so this is not polled by default.
 */
export function useInterviewTranscript(
  id: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: interviewKeys.detail(id ?? ""),
    queryFn: () => interviewsApi.get(id as string),
    enabled: (options?.enabled ?? true) && !!id,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Starts an interview linked to a job. Invalidates the interview lists so a
 * freshly started interview shows up in resume/history views.
 */
export function useCreateInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInterviewInput) => interviewsApi.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: interviewKeys.lists() });
    },
  });
}

/**
 * Reopens a completed interview (completed → in_progress) so the user can enrich
 * it. Reopening reactivates the live-transcript path, so we invalidate both the
 * interview lists AND the mailbox job cache — the linked job view then reflects
 * the reactivated interview.
 */
export function useReopenInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => interviewsApi.reopen(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: interviewKeys.all });
      void queryClient.invalidateQueries({ queryKey: mailboxKeys.jobs() });
    },
  });
}

/**
 * Archives (discards) an interview — the escape hatch for a stuck in-progress
 * interview whose driving job failed. Invalidates the interview lists so resume
 * detection (the run-on-machine dialog) immediately stops offering it, plus the
 * mailbox job cache so the linked job view reflects the discarded state.
 */
export function useArchiveInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => interviewsApi.archive(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: interviewKeys.all });
      void queryClient.invalidateQueries({ queryKey: mailboxKeys.jobs() });
    },
  });
}

/**
 * Continues a completed interview (ENG-148): dispatches a new seeded job, copies
 * the prior transcript into it, and re-points the interview at it. Returns
 * `{ interview, jobId }`; callers navigate to `/mailbox/jobs/<jobId>` on success.
 * A NEW job appears and the interview flips back to in_progress, so we invalidate
 * both the interview lists AND the mailbox job cache.
 */
export function useContinueInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => interviewsApi.continue(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: interviewKeys.all });
      void queryClient.invalidateQueries({ queryKey: mailboxKeys.jobs() });
    },
  });
}
